'use client';

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { gridIdToPath } from '@/lib/utils';
import { RateLimitError } from '@/lib/rate-limit-error';
import type {
  V2FloorPriceSnapshot,
  V2GridRewardEvent,
} from '@/lib/types/v2';

export interface V2DailyStats {
  id: string;
  gridId: string;
  dayTimestamp: number;
  yieldReceived: string;
  taxCollected: string;
  taxWithdrawn: string;
  seatBuys: number;
  seatAbandons: number;
  seatForfeits: number;
  yieldClaimed: string;
}

interface GridHistoryResponse {
  data: {
    floorPriceSnapshots: V2FloorPriceSnapshot[];
    rewardEvents: V2GridRewardEvent[];
    dailyStats: V2DailyStats[];
  };
}

/**
 * Lazy-loaded analytics arrays for a grid (floor snapshots, reward events,
 * daily stats). Served by `/api/grid/[ugm]/[gridId]/history` — kept out of
 * the hot `/api/grid/[ugm]/[gridId]` payload so the board's first paint
 * doesn't pay for 500+ rows of history no one has asked for yet.
 *
 * Call this from panels / chart tabs only when they're actually mounted —
 * `enabled=false` keeps the query dormant, including when the grid id isn't
 * resolved yet (e.g. `disabled-*` lobby sentinel).
 */
export function useGridHistory(
  gridId: string | undefined,
  enabled: boolean = true,
) {
  const isLiveDisabled = !gridId || gridId.startsWith('disabled-');

  const { data, isLoading, error } = useQuery<GridHistoryResponse>({
    queryKey: ['v2-grid-history', gridId],
    enabled: enabled && !isLiveDisabled,
    queryFn: async () => {
      const res = await fetch(`/api/grid/${gridIdToPath(gridId!)}/history`);
      if (res.status === 429) throw new RateLimitError();
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Failed to fetch grid history (${res.status})`);
      }
      return res.json();
    },
    // History data is near-immutable (reward events + floor snapshots are
    // append-only, daily stats roll once per day) so we cache it generously.
    staleTime: 60_000,
    refetchInterval: 120_000,
  });

  return useMemo(() => ({
    floorPriceSnapshots: data?.data.floorPriceSnapshots ?? [],
    rewardEvents: data?.data.rewardEvents ?? [],
    dailyStats: data?.data.dailyStats ?? [],
    isLoading,
    error,
  }), [data, isLoading, error]);
}

export type UseGridHistoryReturn = ReturnType<typeof useGridHistory>;
